/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectVisualFilters,
  selectHotelName,
  selectPmsSync,
  selectTrendBookingDate,
  selectTrendTargetDate,
  selectWeeklyTrendEndDate,
  selectWeeklyTrendStartDate,
  selectEnableHotelGroups,
  selectReportType,
  selectTabValue,
  selectIsPrimaryHotelSetOverTime,
  selectInvalidPrimaryHotelList,
} from 'modules/dashboard/selectors';
import {
  selectIsCumulative,
  selectTrendTimeWeeklyView,
  selectIsSegmentDetailed,
  selectSegmentGroup1,
  selectSegmentGroup2,
  selectTrendSegmentChartBreakdown,
  selectTrendSegmentFocusOn,
  selectActiveTrendTab,
  selectTabularView,
  selectAdjustedStartDate,
  selectAdjustedEndDate,
  selectGranularity,
  selectMovingAverage,
  selectIsMovingAverage,
  selectIsFocusOnSet,
  selectTrendStartDate,
  selectTrendEndDate,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import {
  getBookingDateSchema,
  getGranularitySchema,
  getTrendGroup1And2Schema,
  getHotelIdSchema,
  getMovingAverageSchema,
  getPmsSyncToggleSchema,
  getTargetDateSchema,
  getDashboardPmsSyncSchema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import { REPORT_TYPE } from 'modules/dashboard/constants';
import writeLog from 'modules/common/utils/filter-log';
import { getTSBreakdownAndFocusOnSchema } from '../functions';
import { GRANULARITY } from '../constants';
/**
 * Custom hook to generate Trend segment widget filter schemas
 */
const useTrendSegment = () => {
  const dispatch = useDispatch();
  //
  const bookingDate = useSelector(selectTrendBookingDate);
  const targetDate = useSelector(selectTrendTargetDate);
  const hotelId = useSelector(selectHotelName);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const targetStartDate = useSelector(selectWeeklyTrendStartDate);
  const targetEndDate = useSelector(selectWeeklyTrendEndDate);
  const isCumulative = useSelector(selectIsCumulative);
  const focusOn = useSelector(selectTrendSegmentFocusOn);
  const chartBreakdown = useSelector(selectTrendSegmentChartBreakdown);
  const isDetailed = useSelector(selectIsSegmentDetailed);
  const segmentGroup1 = useSelector(selectSegmentGroup1);
  const segmentGroup2 = useSelector(selectSegmentGroup2);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const activeTab = useSelector(selectActiveTrendTab);
  const reportType = useSelector(selectReportType);
  const isTabular = useSelector(selectTabularView);
  const adjustedStartDate = useSelector(selectAdjustedStartDate);
  const adjustedEndDate = useSelector(selectAdjustedEndDate);
  const granularity = useSelector(selectGranularity);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const movingAverage = useSelector(selectMovingAverage);
  const tabValue = useSelector(selectTabValue);
  const IsFocusOnSet = useSelector(selectIsFocusOnSet);
  const startDate = useSelector(selectTrendStartDate);
  const endDate = useSelector(selectTrendEndDate);
  const isPrimaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  //
  useEffect(() => {
    // Calculate adjusted stay date
    if (isTabular) {
      if (weeklyViewToggle) {
        dispatch(trendActions.setTrendStartDate(targetStartDate));
        dispatch(trendActions.setTrendEndDate(targetEndDate));
      } else if (granularity === GRANULARITY.DAILY) {
        dispatch(trendActions.setTrendStartDate(targetDate?.startDate));
        dispatch(trendActions.setTrendEndDate(targetDate?.endDate));
      } else {
        dispatch(trendActions.setTrendStartDate(adjustedStartDate));
        dispatch(trendActions.setTrendEndDate(adjustedEndDate));
      }
    } else if (isCumulative) {
      dispatch(trendActions.setTrendStartDate(targetDate?.startDate));
      dispatch(trendActions.setTrendEndDate(targetDate?.endDate));
    } else {
      dispatch(
        trendActions.setTrendStartDate(weeklyViewToggle ? targetStartDate : targetDate?.startDate)
      );
      dispatch(
        trendActions.setTrendEndDate(weeklyViewToggle ? targetEndDate : targetDate?.endDate)
      );
    }
  }, [
    isTabular,
    granularity,
    isCumulative,
    weeklyViewToggle,
    targetStartDate,
    targetEndDate,
    targetDate,
    adjustedEndDate,
    adjustedStartDate,
  ]);
  //
  useEffect(() => {
    if (
      !IsFocusOnSet ||
      !hotelId ||
      reportType !== REPORT_TYPE.TREND_TIME ||
      (!chartBreakdown && !targetDate && !hotelId && !bookingDate)
    ) {
      return;
    }
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    const targetDateFilter = getTargetDateSchema(visualFilters, startDate, endDate);

    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);

    let hotelIdFilter = [];
    if (enableHotelGroups) {
      let hotelListIds;
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
      hotelIdFilter = getHotelIdSchema(hotelListIds, visualFilters);
    } else {
      hotelIdFilter = getHotelIdSchema(hotelId?.hotelCode, visualFilters);
    }
    //
    const segmentAndFocusOnFilter = getTSBreakdownAndFocusOnSchema(
      visualFilters,
      chartBreakdown,
      isDetailed,
      focusOn,
      segmentGroup1,
      segmentGroup2
    );
    const pmsSyncFilter = getPmsSyncToggleSchema(visualFilters, pmsSyncToggle);
    const group1And2Filter = getTrendGroup1And2Schema(visualFilters, segmentGroup1, segmentGroup2);
    const granularityFilter = getGranularitySchema(visualFilters, granularity);
    const movingAverageFilter = getMovingAverageSchema(visualFilters, movingAverage);
    const pmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    //
    if (
      bookingDateFilter &&
      targetDateFilter &&
      hotelIdFilter &&
      segmentAndFocusOnFilter &&
      group1And2Filter
    ) {
      const filters = [
        ...pmsSyncFilter,
        bookingDateFilter,
        targetDateFilter,
        hotelIdFilter,
        ...segmentAndFocusOnFilter,
        ...group1And2Filter,
        movingAverageFilter,
        ...pmsSyncSchema,
      ];
      if (activeTab === REPORT_TYPE.TREND_SEGMENT) {
        writeLog('V2 Trend Segment Filters', filters);
      }
      dispatch(trendActions.updateTrendSegmentFilterList(filters));

      // tabular filter list
      let tabularFilters = [
        ...pmsSyncFilter,
        bookingDateFilter,
        targetDateFilter,
        hotelIdFilter,
        ...segmentAndFocusOnFilter,
        ...group1And2Filter,
        movingAverageFilter,
        ...pmsSyncSchema,
      ];
      if (!weeklyViewToggle) {
        tabularFilters = [...tabularFilters, granularityFilter];
      }
      if (activeTab === REPORT_TYPE.TREND_SEGMENT) {
        writeLog('V2 Trend Segment Tabular Filters', tabularFilters);
      }
      dispatch(trendActions.updateTrendSegmentTabularFilterList(tabularFilters));
    }
  }, [
    isDetailed,
    focusOn,
    chartBreakdown,
    bookingDate,
    visualFilters,
    targetDate,
    hotelId,
    pmsSyncToggle,
    weeklyViewToggle,
    targetStartDate,
    targetEndDate,
    segmentGroup1,
    segmentGroup2,
    isCumulative,
    enableHotelGroups,
    reportType,
    activeTab,
    granularity,
    adjustedStartDate,
    adjustedEndDate,
    isTabular,
    isMovingAverage,
    movingAverage,
    tabValue,
    IsFocusOnSet,
    startDate,
    endDate,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
  ]);
};
//
export default useTrendSegment;
